import { FeatureFlag } from 'core/enums/feature-flag';
import { ActionOverlays } from 'core/enums/overlay/action-overlays';

export enum ActionContext {
  Account,
  SubscriptionDetail,
  AddonDetail,
  ShowmaxAddonDetail,
  ShowmaxEplAddonDetail,
  BritboxAddonDetail,
  HardwareDetail,
  DecoderInsuranceDetail,
  InsuranceDetail,
  InternetFLTEDetail,
  InternetFibreDetail,
  InsuranceDetailFuneral,
  InsuranceDetailSubscription,
  InsuranceDetailDebtWaiver,
  InsuranceDeviceCarePlan,
}

export enum AvailableProductAction {
  Activate = 'Activation Link',
  ShowmaxActivateEPL = 'ShowmaxEPL Activation Link',
  ShowmaxActivate = 'Showmax Activation Link',
  LinkAddonPackages = 'Link Addon Packages',
  LinkPackage = 'Link Packages',
  LinkDishlessPackage = 'Link Dishless Package',
  ChangePackage = 'Change Package',
  ChangeDishlessPackage = 'Change Dishless Package',
  ReconnectPackage = 'Reconnect Package',
  ReconnectDishlessPackage = 'Reconnect Dishless Package',
  DisconnectPackage = 'Disconnect Packages',
  DisconnectDishlessPackage = 'Disconnect Dishless Packages',
  CancelShowmax = 'Cancel Showmax',
  CancelShowmaxEpl = 'Cancel ShowmaxEPL',
  CancelBritbox = 'Cancel Britbox',
  MonthlyToBundle = 'Monthly To Bundle',
  ChangeMethodPayment = 'Change Method of Payment',
  StreamMonthlyToBundle = 'Stream Monthly To Bundle',
  RenewBundle = 'Renew Bundle',
  Recontract = 'Recontract',
  RedeemVoucher = 'Redeem Voucher',
  SwitchToStream = 'Switch To Stream',
  GetQuote = 'Get Quote',
  CaptureBundle = 'Capture Bundle',
  CreateXtraview = 'Create Xtraview',
  BreakXtraview = 'Break Xtraview',
  InsureDecoder = 'Insure Decoder',
  CancelInsurance = 'Cancel Insurance',
  CancelPolicy = 'Cancel Policy',
  ChangeBeneficiary = 'Update Beneficiary',
  EditMembers = 'Update Lives Insured',
  AddBeneficiary = 'Add Beneficiary',
  WhatsCovered = 'Whats Covered',
  PolicyWordingFuneral = 'Policy Wording Funeral',
  PolicyWordingSubscription = 'Policy Wording Subscription',
  PolicyWordingDebt = 'Policy Wording Debt',
  InsuranceDeviceCarePlan = 'Device Care Plan',
  PolicyWordingDecoder = 'Policy Wording Decoder',
  CodeOfConduct = 'Code of Conduct',
  ComplaintsPolicy = 'Complaints Policy',
  SubmitClaim = 'Submit Claim',
  ClearErrorCode = 'Clear Error Code',
  ViewLinked = 'View linked package',
  ProductHighlights = 'Product Highlights',
  OtherAddOns = 'Other Add-Ons',
  ViewInsurancePolicy = 'View Insurance Policy',
  ViewLinkedProduct = 'View Linked Product',
  WatchNow = 'Watch Now',
  ManageExtraStream = 'Manage Mobile Stream',
  DisconnectAll = 'Disconnect All', // TODO: Add UI?
  SpeakToAnAgent = 'Speak to An Agent',
  RelocateFibre = 'Relocate My Fibre',
  CancelFibreService = 'Cancel Service',
  RecommendedBundles = 'Recommended Bundles',
  // Internet FLTE
  TopUP = 'TopUP',
  SetInternetSpendLimit = 'Set Internet Spend Limit',
  DataTransfer = 'Data Transfer',
  DisconnectPackages = 'Disconnect Packages',
  UpdateDeviceName = 'Update Device Name',
  LocationUpdate = 'Location Update',
  BalanceEnquiry = 'Balance Enquiry',
  GetSIMStatus = 'Get SIM Status',
  ChangeInternetPackage = 'Change Internet Package',
  DisconnectInternetPackages = 'Disconnect Internet Packages',
  ReconnectInternetPackages = 'Reconnect Internet Packages',
  // VAS
  LinkDisney = 'Link Disney',
  LinkMovies = 'Link Movies',
  LinkShowmax = 'Link Showmax',
  LinkStream = 'Link Stream',
}

export type ActionExpanded = {
  action: ActionOverlays;
  feature: FeatureFlag;
  apiAction: AvailableProductAction | null;
  context: ActionContext[];
  href?: string;
};

type ActionMapType = Map<AvailableProductAction, ActionExpanded>;

export const ActionMap: ActionMapType = new Map([
  [
    AvailableProductAction.Activate,
    {
      action: ActionOverlays.PackageActivation,
      feature: FeatureFlag.ActionFeatureActivate,
      apiAction: AvailableProductAction.Activate,
      context: [ActionContext.SubscriptionDetail, ActionContext.ShowmaxAddonDetail],
    },
  ],
  [
    AvailableProductAction.ShowmaxActivateEPL,
    {
      action: ActionOverlays.PackageActivation,
      feature: FeatureFlag.ActionFeatureActivateEPL,
      apiAction: AvailableProductAction.ShowmaxActivateEPL,
      context: [ActionContext.SubscriptionDetail, ActionContext.ShowmaxEplAddonDetail],
    },
  ],
  [
    AvailableProductAction.ShowmaxActivate,
    {
      action: ActionOverlays.PackageActivation,
      feature: FeatureFlag.ActionFeatureActivate,
      apiAction: AvailableProductAction.ShowmaxActivate,
      context: [ActionContext.SubscriptionDetail, ActionContext.ShowmaxAddonDetail],
    },
  ],
  [
    AvailableProductAction.LinkPackage,
    {
      action: ActionOverlays.LinkPackage,
      feature: FeatureFlag.ActionFeatureLinkPackage,
      apiAction: AvailableProductAction.LinkPackage,
      context: [ActionContext.SubscriptionDetail],
    },
  ],
  [
    AvailableProductAction.LinkAddonPackages,
    {
      action: ActionOverlays.LinkPackage,
      feature: FeatureFlag.ActionFeatureLinkPackage,
      apiAction: AvailableProductAction.LinkAddonPackages,
      context: [ActionContext.SubscriptionDetail],
    },
  ],
  [
    AvailableProductAction.LinkDishlessPackage,
    {
      action: ActionOverlays.LinkPackage,
      feature: FeatureFlag.ActionFeatureLinkPackage,
      apiAction: AvailableProductAction.LinkDishlessPackage,
      context: [ActionContext.SubscriptionDetail],
    },
  ],
  [
    AvailableProductAction.ChangePackage,
    {
      action: ActionOverlays.ChangePackage,
      feature: FeatureFlag.ActionFeatureChangePackage,
      apiAction: AvailableProductAction.ChangePackage,
      context: [ActionContext.SubscriptionDetail],
    },
  ],
  [
    AvailableProductAction.ChangeDishlessPackage,
    {
      action: ActionOverlays.ChangePackage,
      feature: FeatureFlag.ActionFeatureChangeDishlessPackage,
      apiAction: AvailableProductAction.ChangeDishlessPackage,
      context: [ActionContext.SubscriptionDetail],
    },
  ],
  [
    AvailableProductAction.ReconnectPackage,
    {
      action: ActionOverlays.Reconnect,
      feature: FeatureFlag.ActionFeatureReconnectPackage,
      apiAction: AvailableProductAction.ReconnectPackage,
      context: [
        ActionContext.SubscriptionDetail,
        ActionContext.AddonDetail,
        ActionContext.ShowmaxAddonDetail,
        ActionContext.ShowmaxEplAddonDetail,
        ActionContext.BritboxAddonDetail,
      ],
    },
  ],
  [
    AvailableProductAction.ReconnectDishlessPackage,
    {
      action: ActionOverlays.Reconnect,
      feature: FeatureFlag.ActionFeatureReconnectDishlessPackage,
      apiAction: AvailableProductAction.ReconnectDishlessPackage,
      context: [
        ActionContext.SubscriptionDetail,
        ActionContext.AddonDetail,
        ActionContext.ShowmaxAddonDetail,
        ActionContext.ShowmaxEplAddonDetail,
        ActionContext.BritboxAddonDetail,
      ],
    },
  ],
  [
    AvailableProductAction.DisconnectPackage,
    {
      action: ActionOverlays.Disconnect,
      feature: FeatureFlag.ActionFeatureDisconnectPackage,
      apiAction: AvailableProductAction.DisconnectPackage,
      context: [
        ActionContext.SubscriptionDetail,
        ActionContext.AddonDetail,
        ActionContext.ShowmaxAddonDetail,
        ActionContext.ShowmaxEplAddonDetail,
        ActionContext.BritboxAddonDetail,
      ],
    },
  ],
  [
    AvailableProductAction.DisconnectDishlessPackage,
    {
      action: ActionOverlays.Disconnect,
      feature: FeatureFlag.ActionFeatureDisconnectDishlessPackage,
      apiAction: AvailableProductAction.DisconnectDishlessPackage,
      context: [
        ActionContext.SubscriptionDetail,
        ActionContext.AddonDetail,
        ActionContext.ShowmaxAddonDetail,
        ActionContext.ShowmaxEplAddonDetail,
        ActionContext.BritboxAddonDetail,
      ],
    },
  ],
  // [AvailableProductAction.CancelShowmaxEpl, { action: ActionOverlays.CancelShowmax, feature: FeatureFlag.ActionFeatureCancelShowmax, apiAction: AvailableProductAction.CancelShowmaxEpl, context: [ActionContext.ShowmaxAddonDetail, ActionContext.ShowmaxEplAddonDetail] }],
  // [AvailableProductAction.CancelShowmax, { action: ActionOverlays.CancelShowmax, feature: FeatureFlag.ActionFeatureCancelShowmax, apiAction: AvailableProductAction.CancelShowmax, context: [ActionContext.ShowmaxAddonDetail, ActionContext.ShowmaxEplAddonDetail] }],
  // [AvailableProductAction.CancelBritbox, { action: ActionOverlays.CancelShowmax, feature: FeatureFlag.ActionFeatureCancelBritbox, apiAction: AvailableProductAction.CancelBritbox, context: [ActionContext.BritboxAddonDetail] }],
  [
    AvailableProductAction.MonthlyToBundle,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureMonthlyToBundle,
      apiAction: AvailableProductAction.MonthlyToBundle,
      context: [ActionContext.SubscriptionDetail],
    },
  ],
  [
    AvailableProductAction.StreamMonthlyToBundle,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureStreamMonthlyToBundle,
      apiAction: AvailableProductAction.StreamMonthlyToBundle,
      context: [ActionContext.SubscriptionDetail],
    },
  ],
  [
    AvailableProductAction.RenewBundle,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureStreamRenewBundle,
      apiAction: AvailableProductAction.RenewBundle,
      context: [ActionContext.SubscriptionDetail],
    },
  ],
  [
    AvailableProductAction.Recontract,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureRecontractBundle,
      apiAction: AvailableProductAction.Recontract,
      context: [ActionContext.SubscriptionDetail],
    },
  ],
  [
    AvailableProductAction.RedeemVoucher,
    {
      action: ActionOverlays.RedeemVoucher,
      feature: FeatureFlag.ActionFeatureRedeemVoucher,
      apiAction: null,
      context: [ActionContext.SubscriptionDetail],
    },
  ],
  [
    AvailableProductAction.SwitchToStream,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureSwitchToStream,
      apiAction: AvailableProductAction.SwitchToStream,
      context: [],
    },
  ],
  [
    AvailableProductAction.GetQuote,
    {
      action: ActionOverlays.Reconnect,
      feature: FeatureFlag.ActionFeatureGetQuote,
      apiAction: AvailableProductAction.GetQuote,
      context: [ActionContext.SubscriptionDetail, ActionContext.AddonDetail],
    },
  ],
  [
    AvailableProductAction.CaptureBundle,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureCaptureBundle,
      apiAction: AvailableProductAction.CaptureBundle,
      context: [ActionContext.SubscriptionDetail],
    },
  ],
  [
    AvailableProductAction.CreateXtraview,
    {
      action: ActionOverlays.XtraViewLink,
      feature: FeatureFlag.ActionFeatureCreateXtraview,
      apiAction: AvailableProductAction.CreateXtraview,
      context: [ActionContext.HardwareDetail],
    },
  ],
  [
    AvailableProductAction.BreakXtraview,
    {
      action: ActionOverlays.XtraViewUnlink,
      feature: FeatureFlag.ActionFeatureBreakXtraview,
      apiAction: AvailableProductAction.BreakXtraview,
      context: [ActionContext.HardwareDetail],
    },
  ],
  [
    AvailableProductAction.InsureDecoder,
    {
      action: ActionOverlays.InsuranceAdd,
      feature: FeatureFlag.ActionFeatureInsureDecoder,
      apiAction: AvailableProductAction.InsureDecoder,
      context: [ActionContext.HardwareDetail],
    },
  ],
  [
    AvailableProductAction.ChangeBeneficiary,
    {
      action: ActionOverlays.InsuranceManageBeneficiary,
      feature: FeatureFlag.ActionFeatureManageBeneficiaries,
      apiAction: AvailableProductAction.ChangeBeneficiary,
      context: [
        ActionContext.InsuranceDetail,
        ActionContext.InsuranceDetailFuneral,
        ActionContext.InsuranceDetailDebtWaiver,
        ActionContext.InsuranceDetailSubscription,
      ],
    },
  ],
  [
    AvailableProductAction.EditMembers,
    {
      action: ActionOverlays.InsuranceManageFamilyMembers,
      feature: FeatureFlag.ActionFeatureManageMembers,
      apiAction: AvailableProductAction.EditMembers,
      context: [ActionContext.InsuranceDetailFuneral],
    },
  ],
  [
    AvailableProductAction.AddBeneficiary,
    {
      action: ActionOverlays.InsuranceManageBeneficiary,
      feature: FeatureFlag.ActionFeatureAddBeneficiary,
      apiAction: null,
      context: [
        ActionContext.InsuranceDetail,
        ActionContext.InsuranceDetailFuneral,
        ActionContext.InsuranceDetailDebtWaiver,
        ActionContext.InsuranceDetailSubscription,
      ],
    },
  ],
  [
    AvailableProductAction.CancelInsurance,
    {
      action: ActionOverlays.InsuranceCancel,
      feature: FeatureFlag.ActionFeatureCancelInsurance,
      apiAction: AvailableProductAction.CancelInsurance,
      context: [ActionContext.DecoderInsuranceDetail],
    },
  ],
  [
    AvailableProductAction.CancelPolicy,
    {
      action: ActionOverlays.InsuranceCancel,
      feature: FeatureFlag.ActionFeatureCancelInsurance,
      apiAction: AvailableProductAction.CancelPolicy,
      context: [
        ActionContext.InsuranceDetail,
        ActionContext.InsuranceDetailFuneral,
        ActionContext.InsuranceDetailDebtWaiver,
        ActionContext.InsuranceDetailSubscription,
      ],
    },
  ],
  [
    AvailableProductAction.SubmitClaim,
    {
      action: ActionOverlays.InsuranceClaim,
      feature: FeatureFlag.ActionFeatureSubmitClaim,
      apiAction: null,
      context: [ActionContext.DecoderInsuranceDetail],
    },
  ],
  // [AvailableProductAction.WhatsCovered, { action: ActionOverlays.None, feature: FeatureFlag.ActionFeatureWhatsCovered, apiAction: null, context: [ActionContext.InsuranceDetail, ActionContext.DecoderInsuranceDetail, ActionContext.InsuranceDetailFuneral, ActionContext.InsuranceDetailDebtWaiver,ActionContext.InsuranceDetailSubscription] }],
  [
    AvailableProductAction.PolicyWordingFuneral,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureFuneralPolicyDocuments,
      apiAction: null,
      context: [ActionContext.InsuranceDetailFuneral],
    },
  ],
  [
    AvailableProductAction.PolicyWordingSubscription,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureSubscriptionPolicyDocuments,
      apiAction: null,
      context: [ActionContext.InsuranceDetailSubscription],
    },
  ],
  [
    AvailableProductAction.PolicyWordingDebt,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureDebtWaiverPolicyDocuments,
      apiAction: null,
      context: [ActionContext.InsuranceDetailDebtWaiver],
    },
  ],
  [
    AvailableProductAction.PolicyWordingDecoder,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureDecoderPolicyDocuments,
      apiAction: null,
      context: [ActionContext.DecoderInsuranceDetail],
    },
  ],
  [
    AvailableProductAction.InsuranceDeviceCarePlan,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureDeviceCarePlan,
      apiAction: null,
      context: [ActionContext.InsuranceDeviceCarePlan],
    },
  ],
  [
    AvailableProductAction.CodeOfConduct,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureCodeOfConduct,
      apiAction: null,
      context: [
        ActionContext.InsuranceDetail,
        ActionContext.DecoderInsuranceDetail,
        ActionContext.InsuranceDetailFuneral,
        ActionContext.InsuranceDetailDebtWaiver,
        ActionContext.InsuranceDetailSubscription,
      ],
    },
  ],
  [
    AvailableProductAction.ComplaintsPolicy,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureComplaintsPolicy,
      apiAction: null,
      context: [
        ActionContext.InsuranceDetail,
        ActionContext.DecoderInsuranceDetail,
        ActionContext.InsuranceDetailFuneral,
        ActionContext.InsuranceDetailDebtWaiver,
        ActionContext.InsuranceDetailSubscription,
      ],
    },
  ],
  [
    AvailableProductAction.SpeakToAnAgent,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureSpeakToAnAgent,
      apiAction: null,
      context: [ActionContext.InternetFibreDetail, ActionContext.InternetFLTEDetail],
    },
  ],
  [
    AvailableProductAction.RelocateFibre,
    {
      action: ActionOverlays.InternetRelocateFibre,
      feature: FeatureFlag.ActionFeatureRelocateFibre,
      apiAction: null,
      context: [ActionContext.InternetFibreDetail],
    },
  ],
  [
    AvailableProductAction.CancelFibreService,
    {
      action: ActionOverlays.InternetFibreCancel,
      feature: FeatureFlag.ActionFeatureCancelFibreService,
      apiAction: null,
      context: [ActionContext.InternetFibreDetail],
    },
  ],
  [
    AvailableProductAction.ClearErrorCode,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureClearErrorCode,
      apiAction: AvailableProductAction.ClearErrorCode,
      context: [ActionContext.SubscriptionDetail, ActionContext.HardwareDetail],
    },
  ],
  [
    AvailableProductAction.ViewLinked,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureViewLinked,
      apiAction: null,
      context: [
        ActionContext.AddonDetail,
        ActionContext.ShowmaxAddonDetail,
        ActionContext.ShowmaxEplAddonDetail,
        ActionContext.BritboxAddonDetail,
      ],
    },
  ],
  [
    AvailableProductAction.ProductHighlights,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureProductHighlights,
      apiAction: null,
      context: [ActionContext.AddonDetail],
    },
  ],
  [
    AvailableProductAction.OtherAddOns,
    {
      action: ActionOverlays.LinkPackage,
      feature: FeatureFlag.ActionFeatureOtherAddOns,
      apiAction: null,
      context: [
        ActionContext.AddonDetail,
        ActionContext.ShowmaxAddonDetail,
        ActionContext.ShowmaxEplAddonDetail,
        ActionContext.BritboxAddonDetail,
      ],
    },
  ],
  [
    AvailableProductAction.ViewInsurancePolicy,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureViewInsurancePolicy,
      apiAction: null,
      context: [ActionContext.HardwareDetail],
    },
  ],
  [
    AvailableProductAction.ViewLinkedProduct,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureViewLinkProduct,
      apiAction: null,
      context: [ActionContext.HardwareDetail],
    },
  ],
  [
    AvailableProductAction.WatchNow,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureWatchNow,
      apiAction: null,
      context: [ActionContext.ShowmaxAddonDetail, ActionContext.ShowmaxEplAddonDetail],
    },
  ],
  [
    AvailableProductAction.ManageExtraStream,
    {
      action: ActionOverlays.ExtraStream,
      feature: FeatureFlag.ActionFeatureExtraStream,
      apiAction: null,
      context: [ActionContext.SubscriptionDetail, ActionContext.AddonDetail],
    },
  ],
  // Internet FLTE
  [
    AvailableProductAction.TopUP,
    {
      action: ActionOverlays.InternetRechargeData,
      feature: FeatureFlag.ActionFeatureTopUP,
      apiAction: AvailableProductAction.TopUP,
      context: [ActionContext.InternetFLTEDetail],
    },
  ],
  [
    AvailableProductAction.SetInternetSpendLimit,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureSetInternetSpeedLimit,
      apiAction: AvailableProductAction.SetInternetSpendLimit,
      context: [ActionContext.InternetFLTEDetail],
    },
  ],
  [
    AvailableProductAction.ChangeInternetPackage,
    {
      action: ActionOverlays.InternetChangePackage,
      feature: FeatureFlag.ActionFeatureChangeInternetPackage,
      apiAction: AvailableProductAction.ChangeInternetPackage,
      context: [ActionContext.InternetFLTEDetail],
    },
  ],
  [
    AvailableProductAction.DisconnectInternetPackages,
    {
      action: ActionOverlays.InternetDisconnect,
      feature: FeatureFlag.ActionFeatureInternetDisconnect,
      apiAction: AvailableProductAction.DisconnectInternetPackages,
      context: [ActionContext.InternetFLTEDetail],
    },
  ],
  [
    AvailableProductAction.ReconnectInternetPackages,
    {
      action: ActionOverlays.InternetReconnect,
      feature: FeatureFlag.ActionFeatureInternetReconnect,
      apiAction: AvailableProductAction.ReconnectInternetPackages,
      context: [ActionContext.InternetFLTEDetail],
    },
  ],
  [
    AvailableProductAction.DataTransfer,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureDataTransfer,
      apiAction: AvailableProductAction.DataTransfer,
      context: [ActionContext.InternetFLTEDetail],
    },
  ],
  [
    AvailableProductAction.UpdateDeviceName,
    {
      action: ActionOverlays.InternetDeviceNameUpdate,
      feature: FeatureFlag.ActionFeatureUpdateDeviceName,
      apiAction: AvailableProductAction.UpdateDeviceName,
      context: [ActionContext.InternetFLTEDetail],
    },
  ],
  [
    AvailableProductAction.LocationUpdate,
    {
      action: ActionOverlays.InternetUpdateLocation,
      feature: FeatureFlag.ActionFeatureLocationUpdate,
      apiAction: AvailableProductAction.LocationUpdate,
      context: [ActionContext.InternetFLTEDetail],
    },
  ],
  [
    AvailableProductAction.BalanceEnquiry,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureBalanceEnquiry,
      apiAction: AvailableProductAction.BalanceEnquiry,
      context: [ActionContext.InternetFLTEDetail],
    },
  ],
  [
    AvailableProductAction.GetSIMStatus,
    {
      action: ActionOverlays.None,
      feature: FeatureFlag.ActionFeatureGetSimStatus,
      apiAction: AvailableProductAction.GetSIMStatus,
      context: [ActionContext.InternetFLTEDetail],
    },
  ],
]);